import { useState, useEffect } from 'react'
import { Box, Stack, Text, Badge, Alert, Progress, Spinner } from '@/components/lib'
import PromisForm from './PromisForm'
import api from '../../api'

// ---------------------------------------------------------------------------
// PROMIS battery
//
// A battery is an ordered collection of PROMIS forms administered one after
// another. The Assessment Center API administers each instrument as its own
// (StatelessParticipants) session, so this component walks the battery's member
// forms in order, delegating each leg to the single-form <PromisForm> engine and
// accumulating a per-form result.
//
// Once every form is complete, the collected sessions are posted to the server,
// which registers the battery instance and calls the official BatteryScore
// endpoint to compute the validated battery-level PROPr health-utility score
// (only possible when the battery contains the seven PROPr domains).
//
// Progress (per-form results + the battery score) is persisted in localStorage
// so a battery survives a page reload; each leg's own in-progress session is
// resumed independently by <PromisForm>.
// ---------------------------------------------------------------------------

export default function PromisBattery ({ participant, dataType, value, onChange }) {
  const forms = dataType?.promisForms || []
  const storageKey = `promis_battery_${dataType?._id}${participant?._id ? `_${participant._id}` : ''}`
  const scoreKey = `${storageKey}_score`

  const [results, setResults] = useState(() => {
    try {
      const saved = JSON.parse(window.sessionStorage.getItem(storageKey))
      if (saved && Object.keys(saved).length) return saved
    } catch { /* ignore malformed cache */ }
    // Seed from a previously-saved value (e.g. reopening a completed battery)
    if (value?.forms?.length) {
      const seeded = {}
      value.forms.forEach(f => {
        if (f.formOid) seeded[f.formOid] = { promisSession: f.promisSession, tScore: f.tScore, stdError: f.stdError, itemCount: f.itemCount }
      })
      return seeded
    }
    return {}
  })

  // The battery-level score result (server response), or { scorable: false } when
  // the battery is not PROPr-composed. null until scoring has run.
  const [batteryScore, setBatteryScore] = useState(() => {
    try { return JSON.parse(window.sessionStorage.getItem(scoreKey)) || null } catch { return null }
  })
  const [scoring, setScoring] = useState(false)
  const [scoreError, setScoreError] = useState(null)

  useEffect(() => {
    window.sessionStorage.setItem(storageKey, JSON.stringify(results))
  }, [results])

  useEffect(() => {
    if (batteryScore) window.sessionStorage.setItem(scoreKey, JSON.stringify(batteryScore))
    else window.sessionStorage.removeItem(scoreKey)
  }, [batteryScore])

  // The per-leg session/token keys must match the ones <PromisForm> builds here;
  // used to read each leg's capability token when scoring the battery.
  function legSessionKey (formOid) {
    return `promis_session_${dataType?._id}${participant?._id ? `_${participant._id}` : ''}_${formOid}`
  }
  function legTokenKey (formOid) {
    return `${legSessionKey(formOid)}_token`
  }

  function emitValue (score) {
    if (!onChange) return
    const out = {
      promisBattery: true,
      forms: forms.map(f => ({ formOid: f.oid, formName: f.name, ...results[f.oid] }))
    }
    if (score && score._id) out.batterySession = score._id
    onChange(out)
  }

  const currentIndex = forms.findIndex(f => !results[f.oid])
  const allComplete = forms.length > 0 && currentIndex === -1

  // Once all forms are complete, compute the battery-level score (once).
  useEffect(() => {
    if (!allComplete || batteryScore || scoring || scoreError) return
    setScoring(true)
    // Prove we hold each member session by presenting its capability token.
    const sessions = forms
      .map(f => ({ id: results[f.oid]?.promisSession, accessToken: window.sessionStorage.getItem(legTokenKey(f.oid)) }))
      .filter(s => s.id && s.accessToken)
    api.promis.scoreBattery(dataType._id, { sessions }, res => {
      setScoring(false)
      setBatteryScore(res)
    }, err => {
      setScoring(false)
      setScoreError(err.message || 'Unable to calculate the battery score.')
    })
  }, [allComplete, batteryScore, scoring, scoreError])

  // Emit the recorded value once scoring has resolved (success, error, or
  // not-scorable). While scoring is in flight we hold off so a partial value
  // isn't submitted.
  useEffect(() => {
    if (!allComplete || scoring) return
    emitValue(batteryScore)
  }, [allComplete, scoring, batteryScore])

  if (!forms.length) {
    return <Alert status='warning'>This PROMIS battery has no forms configured.</Alert>
  }

  if (allComplete) {
    const scored = batteryScore && batteryScore._id
    return (
      <Stack gap={3}>
        <Alert status='success' title='Battery complete'>
          Thank you for completing all {forms.length} questionnaire{forms.length !== 1 ? 's' : ''}.
        </Alert>

        {/* While finalising we still call the scoring endpoint (so PROPr is
            stored for researchers) but only hint at "score" wording when scores
            are shown to the participant. */}
        {scoring &&
          <Box display='flex' alignItems='center' gap={3}>
            <Spinner />
            <Text fontSize='sm' color='fg.muted'>
              {dataType?.promisShowTScore ? 'Calculating battery score…' : 'Finishing…'}
            </Text>
          </Box>}

        {/* Only the overall PROPr is surfaced to the participant, and only when
            it was actually calculated AND the data type is configured to show
            scores. We deliberately do NOT list the individual member forms (AC
            guidance: don't reveal measure names), nor per-form T-scores, nor any
            "couldn't calculate" / error messaging. */}
        {dataType?.promisShowTScore && scored && batteryScore.propr != null &&
          <Box display='flex' alignItems='center' gap={2}>
            <Text fontSize='sm' fontWeight='medium'>PROPr score:</Text>
            <Badge colorPalette='purple' size='lg'>{batteryScore.propr}</Badge>
          </Box>}
      </Stack>
    )
  }

  const currentForm = forms[currentIndex]
  const completedCount = forms.filter(f => results[f.oid]).length

  return (
    <Stack gap={4}>
      <Progress bg='bg.muted' colorPalette='blue' value={(completedCount / forms.length) * 100} size='xs' />

      <PromisForm
        key={currentForm.oid}
        participant={participant}
        dataType={dataType}
        formOid={currentForm.oid}
        beginLabel={currentIndex === 0 ? undefined : 'Continue to next set of questions'}
        introText={currentIndex === 0 ? undefined : 'Here is the next questionnaire in this battery. The questions adjust based on your answers and will complete automatically.'}
        onChange={legResult => setResults(prev => ({ ...prev, [currentForm.oid]: legResult }))}
      />
    </Stack>
  )
}
